<template>
  <div class="relative">
    <Listbox as="div" class="relative">
      <!-- Search Input -->
      <div class="relative">
        <div class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
          <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
        <input
          v-model="search_term"
          :disabled="disabled"
          autocomplete="off"
          :placeholder="translatedPlaceholder"
          type="text"
          :class="[{ 'w-[400px]': shortKing, 'w-[500px]': !shortKing }, { 'bg-gray-100 cursor-not-allowed': disabled }]"
          class="pl-10 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 text-sm"
          @click="show_results = true"
          @focus="show_results = true">

        <!-- Clear button -->
        <button
          v-if="search_term && !disabled"
          class="absolute inset-y-0 right-0 flex items-center pr-3"
          @click="
            () => {
              search_term = ''
              show_results = false
            }
          ">
          <svg class="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-pointer" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <!-- Dropdown Panel -->
      <transition
        enter-active-class="transition duration-200 ease-out"
        enter-from-class="opacity-0 scale-95"
        enter-to-class="opacity-100 scale-100"
        leave-active-class="transition duration-150 ease-in"
        leave-from-class="opacity-100 scale-100"
        leave-to-class="opacity-0 scale-95">
        <div
          v-if="show_results"
          :class="[{ 'w-[400px]': shortKing, 'w-[500px]': !shortKing }]"
          class="absolute z-50 mt-2 bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 max-h-80 overflow-hidden">
          <!-- Close button -->
          <div class="absolute top-4 right-4 z-10">
            <button
              class="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors duration-200"
              @click.stop="show_results = false">
              <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <!-- Tab Navigation -->
          <div v-if="!survey" class="border-b-[1px] pt-2 bg-gray-50 dark:bg-gray-750 border-gray-200 dark:border-gray-700">
            <nav class="flex -mb-1 space-x-4 px-4">
              <div
                :class="[
                  active_tab === 'team_members'
                    ? 'text-blue-600 dark:text-blue-400'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-300'
                ]"
                class="flex justify-start items-center whitespace-nowrap py-3 mb-1 px-0 border-b-[2px] relative transition ease-in-out duration-200 text-sm font-medium select-none border-transparent cursor-pointer"
                @click="handleTabClick('team_members')">
                <!-- Active bottom div for highlighting -->
                <div
                  :class="[
                    active_tab === 'team_members' ? '' : 'scale-0',
                    'absolute pointer-events-none transition transform ease-in-out duration-200 w-full h-[2px] rounded-full bg-blue-600 dark:bg-blue-400 -bottom-0.5'
                  ]" />
                <svg
                  class="w-4 h-4 mr-2"
                  :class="active_tab === 'team_members' ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400'"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                </svg>
                <span class="truncate">{{ $t("People") }}</span>
              </div>

              <div
                v-if="inPlaybook"
                :class="[
                  active_tab === 'dynamic'
                    ? 'text-blue-600 dark:text-blue-400'
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-300'
                ]"
                class="flex justify-start items-center whitespace-nowrap py-3 mb-1 px-0 border-b-[2px] relative transition ease-in-out duration-200 text-sm font-medium select-none border-transparent cursor-pointer"
                @click="handleTabClick('dynamic')">
                <!-- Active bottom div for highlighting -->
                <div
                  :class="[
                    active_tab === 'dynamic' ? '' : 'scale-0',
                    'absolute pointer-events-none transition transform ease-in-out duration-200 w-full h-[2px] rounded-full bg-blue-600 dark:bg-blue-400 -bottom-0.5'
                  ]" />
                <svg
                  class="w-4 h-4 mr-2"
                  :class="active_tab === 'dynamic' ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400'"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
                <span class="truncate">{{ $t("Dynamic") }}</span>
              </div>
            </nav>
          </div>

          <!-- Content Area -->
          <div class="overflow-y-auto max-h-64">
            <!-- Empty States -->
            <div v-if="active_tab === 'team_members' && !filtered_team_members.length" class="px-6 py-8 text-center">
              <div class="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg class="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
                </svg>
              </div>
              <h3 class="text-sm font-medium text-gray-900 dark:text-white mb-2">
                {{ $t("No team members found") }}
              </h3>
              <p class="text-xs text-gray-500 dark:text-gray-400">
                {{ $t("No team members match your search query") }}
              </p>
            </div>

            <div v-else-if="active_tab === 'dynamic' && !senders.length" class="px-6 py-8 text-center">
              <div class="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg class="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 class="text-sm font-medium text-gray-900 dark:text-white mb-2">
                {{ $t("No dynamic options") }}
              </h3>
              <p class="text-xs text-gray-500 dark:text-gray-400">
                {{ $t("No dynamic senders are available") }}
              </p>
            </div>

            <!-- Team Members List -->
            <div v-else-if="active_tab === 'team_members'" class="py-2">
              <ListboxOption
                v-for="user in !survey
                  ? filtered_team_members
                  : senders_clone
                    .filter((sender) => sender.value !== selectedSender?.value)
                    .filter((s) => (search_term ? s.label.toLowerCase().includes(search_term) : true))"
                :key="user.id"
                as="template"
                @click="handleSelect(user)">
                <div
                  class="flex items-center px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors duration-200 group">
                  <div class="flex-shrink-0">
                    <img
                      v-if="user.source === 'integration' && user.logo_url"
                      :src="user.logo_url"
                      class="w-8 h-8 rounded-full object-cover bg-white border border-gray-200 dark:border-gray-600"
                      :alt="getSenderDisplayName(user)"
                      loading="lazy">
                    <Avatar
                      v-else
                      :name="getSenderDisplayName(user)"
                      :first-name="user.first_name"
                      :last-name="user.last_name"
                      :email="getSenderEmail(user)"
                      :show-tooltip="false"
                      :radius="32" />
                  </div>
                  <div class="ml-3 flex-1 min-w-0">
                    <div
                      class="text-sm font-medium text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-200">
                      {{ getSenderDisplayName(user) }}
                    </div>
                    <div class="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      {{ getSenderEmail(user) }}
                    </div>
                  </div>
                  <div class="flex-shrink-0">
                    <svg
                      class="w-4 h-4 text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-200"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </ListboxOption>
            </div>

            <!-- Dynamic List -->
            <div v-else-if="active_tab === 'dynamic'" class="py-2">
              <!-- Warning Note -->
              <div class="px-4 py-3 bg-yellow-50 dark:bg-yellow-900/20 border-l-4 border-yellow-400 mb-2">
                <div class="flex">
                  <div class="flex-shrink-0">
                    <svg class="h-4 w-4 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.232 16.5c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                  </div>
                  <div class="ml-3">
                    <p class="text-xs text-yellow-800 dark:text-yellow-200">
                      {{ $t("Note: If the owner doesn't exist or doesn't have an email linked, the email send will fail.") }}
                    </p>
                  </div>
                </div>
              </div>

              <ListboxOption
                v-for="sender in senders_clone
                  .filter((sender) => sender.value !== selectedSender?.value)
                  .filter((s) => !s.dynamic_metric_selection)
                  .filter((o) => o.value.includes('.owner.id'))
                  .filter((s) => (search_term ? s.label.toLowerCase().includes(search_term) : true))"
                :key="sender.value"
                as="template"
                @click="handleSelect(sender)">
                <div
                  class="flex items-center px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors duration-200 group">
                  <div class="flex-shrink-0">
                    <div class="w-8 h-8 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center">
                      <svg class="w-4 h-4 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                      </svg>
                    </div>
                  </div>
                  <div class="ml-3 flex-1 min-w-0">
                    <div
                      class="text-sm font-medium text-gray-900 dark:text-white group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-200">
                      {{
                        sender.label.replace(
                          $utils.capitalize(
                            all_metrics?.find(
                              (m) => m.reference_name == sender?.value?.split(".")[sender.value.split(".").length - 1]?.replace("}", "")
                            )?.name
                          ),
                          ""
                        )
                      }}
                    </div>
                  </div>
                  <div class="flex-shrink-0">
                    <svg
                      class="w-4 h-4 text-gray-400 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors duration-200"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                    </svg>
                  </div>
                </div>
              </ListboxOption>
            </div>
          </div>
        </div>
      </transition>
    </Listbox>

    <!-- Backdrop -->
    <div v-if="show_results" class="fixed inset-0 z-40" @click="show_results = false" />
  </div>
</template>

<script>
import { Listbox, ListboxOption } from "@headlessui/vue"

import json_data from "@/components/shared/json-files/all-obj-fields.json"
import Avatar from "@/components/shared/Avatar.vue"

export default {
  components: {
    Avatar,
    Listbox,
    ListboxOption
  },
  props: {
    survey: null,
    shortKing: {
      type: Boolean,
      default: false
    },
    inPlaybook: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      required: false,
      default: "Search"
    },
    disabled: {
      type: Boolean,
      default: false
    },
    selectedSender: {
      type: Object,
      default: () => {},
      required: false
    },
    senders: {
      type: Array,
      default: () => [],
      required: false
    }
  },
  emits: ["update:selected-sender"],

  data() {
    return {
      show_results: false,
      search_term: null,
      filtered_team_members: [],
      active_tab: "team_members",
      object_type: "company",
      other_metrics: [],
      forecasted_objects: [],
      senders_clone: []
    }
  },

  computed: {
    translatedPlaceholder() {
      return this.$t(this.placeholder)
    },
    all_metrics() {
      let forecast_metrics = []
      if (this.forecasted_objects?.includes(this.object_type)) {
        forecast_metrics = [
          {
            reference_name: `forecasts__forecast_status`,
            name: this.$t("Forecast Status"),
            data_type: "String",
            input_type: "Select",
            input_options: { choices: ["forecasted", "actual"] },
            object_type: this.object_type
          },
          {
            reference_name: `forecasts__forecast_type`,
            name: this.$t("Forecast Type"),
            data_type: "String",
            input_type: "Select",
            input_options: { choices: ["non-renewable", "renewable"] },
            object_type: this.object_type
          },
          {
            reference_name: `forecasts__forecast_value`,
            name: this.$t("Forecast Value"),
            data_type: "Number",
            input_type: "Number",
            object_type: this.object_type
          }
        ]
      }
      let owner_co_owner_filer = [
        {
          reference_name: `owner__email`,
          name: this.$t("Owner"),
          data_type: "String",
          input_type: "Text",
          object_type: this.object_type
        },
        {
          reference_name: `co_owners__email`,
          name: this.$t("Co-Owners"),
          data_type: "String",
          input_type: "Text",
          object_type: this.object_type
        }
      ]
      let tags_filter_fields = [
        {
          reference_name: `tags__name`,
          name: this.$t("Assigned tags"),
          data_type: "String",
          input_type: "Text",
          object_type: this.object_type
        }
      ]

      const metrics_calc = this.$store.state.metrics
        .concat(forecast_metrics)
        .concat(owner_co_owner_filer)
        .concat(this.other_metrics)
        .concat(tags_filter_fields)

      let metrics_calc_clean = []

      metrics_calc_clean = metrics_calc.filter(
        (m) =>
          !m.reference_name.includes("owner_email") &&
          !m.reference_name.includes("owner_first_name") &&
          !m.reference_name.includes("owner_last_name") &&
          !m.reference_name.includes("owner_name") &&
          m.data_type !== "Plan" &&
          m.data_type !== "Goal" &&
          m.data_type !== "Task" &&
          m.data_type !== "Interaction" &&
          m.data_type !== "Milestone"
      )

      return metrics_calc_clean
    },
    availableSenderAccounts() {
      const integrations = (this.$store.state.integration.integrations || [])
        .filter((i) => (i.name == "outlook" || i.name == "gmail") && i.status == "on")
        .map(this.buildIntegrationSender)
        .filter(Boolean)

      const workspaceUsers = this.buildWorkspaceUserSenders()

      const organizations =
        (this.$store.state.data.workspace_organizations || [])
          .filter((org) => org.id !== this.$store.state.organization.id)
          .map(this.parseOrganizationSender)
          .filter(Boolean) || []

      const defaultSender = this.buildDefaultSender()

      const combined = []
      if (defaultSender) {
        combined.push(defaultSender)
      }
      workspaceUsers.forEach((userSender) => combined.push(userSender))
      organizations.forEach((orgSender) => combined.push(orgSender))
      integrations.forEach((integrationSender) => combined.push(integrationSender))

      const uniqueByKey = []
      const seenKeys = new Set()
      combined.forEach((sender) => {
        if (!sender) {
          return
        }
        const key = sender.id || (sender.email ? `email:${sender.email.toLowerCase()}` : null)
        if (!key) {
          return
        }
        if (!seenKeys.has(key)) {
          seenKeys.add(key)
          uniqueByKey.push(sender)
        }
      })

      return uniqueByKey
    }
  },
  watch: {
    search_term(value) {
      if (value === "," || value === " ") {
        this.search_term = ""
        return
      }

      if (this.search_term) {
        this.filtered_team_members = this.filterSenderAccounts(this.search_term)
        this.show_results = true
      } else {
        this.filtered_team_members = this.availableSenderAccounts
        this.show_results = false
      }
    },
    availableSenderAccounts: {
      immediate: true,
      handler(accounts) {
        if (!this.search_term) {
          this.filtered_team_members = accounts
        } else {
          this.filtered_team_members = this.filterSenderAccounts(this.search_term, accounts)
        }
      }
    }
  },

  created() {
    this.filtered_team_members = this.availableSenderAccounts
    this.init()
  },

  methods: {
    init() {
      this.forecasted_objects = this.$utils
        .find(this.$store.state.settings.modules_settings, {
          reference_code: "renewal_base_metric"
        })
        ?.mapped_objects_metrics?.map((type_map) => {
          return this.$store.state.object_types.find((x) => x.id == type_map.object_type)?.type
        })
      Object.entries(json_data).map((item) => {
        this.other_metrics.push({ ...item[1], object_type: this.object_type })
      })

      this.senders_clone = this.$utils.clone(this.senders)
    },
    handleTabClick(tab) {
      this.show_results = true
      this.active_tab = tab
    },
    handleSelect(item) {
      this.$emit("update:selected-sender", this.$utils.clone(item))
      this.show_results = false
    },
    getSenderDisplayName(user) {
      if (!user) {
        return ""
      }
      if (user.full_name) {
        return user.full_name
      }
      const nameParts = [user.first_name, user.last_name].filter(Boolean).join(" ").trim()
      if (nameParts) {
        return nameParts
      }
      return user.name || user.email || this.$t("Unknown sender")
    },
    getSenderEmail(user) {
      return user?.email || ""
    },
    filterSenderAccounts(term, accounts = this.availableSenderAccounts) {
      const normalizedTerm = term?.toLowerCase().trim()
      if (!normalizedTerm) {
        return accounts
      }
      return accounts.filter((contact) => {
        return contact.email?.toLowerCase().includes(normalizedTerm) || contact.full_name?.toLowerCase().includes(normalizedTerm)
      })
    },
    formatFullName(name) {
      if (!name) {
        return { first_name: "", last_name: "" }
      }
      const parts = name
        .split(/\s+/)
        .map((part) => part.trim())
        .filter(Boolean)
      const first_name = parts.shift() || ""
      const last_name = parts.join(" ")
      return { first_name, last_name }
    },
    parseOrganizationSender(organization) {
      if (!organization?.org_rs_email) {
        return null
      }
      const match = organization.org_rs_email.match(/^\s*(.*?)\s*<([^>]+)>\s*$/)
      let full_name = organization.name || "Revsetter"
      let email = organization.org_rs_email.trim()
      if (match) {
        full_name = match[1]?.trim() || full_name
        email = match[2]?.trim()
      } else if (email.includes("@")) {
        const fallback = email.split("@")[0].replace(/[-_]/g, " ")
        full_name = full_name || fallback
      } else {
        return null
      }
      const { first_name, last_name } = this.formatFullName(full_name)

      return {
        full_name,
        first_name: first_name || full_name,
        last_name,
        id: organization.id,
        email,
        organization_id: organization.id,
        source: "organization"
      }
    },
    buildDefaultSender() {
      const orgEmail = this.$store.state.organization.org_rs_email
      if (!orgEmail) {
        return null
      }
      const match = orgEmail.match(/<([^>]+)>/)
      const email = match ? match[1] : orgEmail
      const name = this.$store.state.organization.name || (email ? email.split("@")[0].replace(/[-_]/g, " ") : "Revsetter")
      const { first_name, last_name } = this.formatFullName(name)
      return {
        full_name: name,
        first_name: first_name || name,
        last_name,
        email,
        id: "revsetter",
        organization_id: this.$store.state.organization.id,
        source: "organization"
      }
    },
    buildIntegrationSender(integration) {
      const email = integration?.user_email
      if (!email) {
        return null
      }
      let user_name = integration.user_name
      if (!user_name) {
        user_name = email.split("@")[0].replace(/[-_]/g, " ")
      }
      const { first_name, last_name } = this.formatFullName(user_name)
      return {
        full_name: user_name,
        first_name: first_name || user_name,
        last_name,
        id: integration.id,
        email,
        provider_label: integration.service_provider || integration.title || integration.name,
        logo_url: integration.logo_url,
        source: "integration"
      }
    },
    buildWorkspaceUserSenders() {
      const currentUserId = this.$store.state.auth.user?.id
      if (!currentUserId) {
        return []
      }
      return (this.$store.state.data.workspace_organizations || [])
        .map((organization) => {
          const member = organization.users?.find((user) => user.id == currentUserId)
          if (!member?.email) {
            return null
          }
          const full_name = member.full_name || `${member.first_name || ""} ${member.last_name || ""}`.trim() || member.email
          const { first_name, last_name } = this.formatFullName(full_name)
          return {
            full_name,
            first_name: member.first_name || first_name || full_name,
            last_name: member.last_name || last_name,
            email: member.email,
            id: `workspace-user-${organization.id}-${member.id}`,
            organization_id: organization.id,
            source: "workspace_user"
          }
        })
        .filter(Boolean)
    }
  }
}
</script>

<style scoped>
.mh-48 {
  max-height: 10rem;
}
</style>
